import { DestroyRef, inject } from '@angular/core';
import type { AbstractControl, FormControl } from '@angular/forms';
import { FormArray, FormGroup } from '@angular/forms';
import type { ErrorStateMatcher } from '@angular/material/core';

type ControlValue<T> = undefined extends T
  ? Exclude<T, undefined> | null
  : null extends T
    ? Exclude<T, null> | null
    : T;

export type NonNullableControlInterface<T> = {
  [K in keyof T]-?: T[K] extends null | undefined
    ? FormControl<NonNullable<T[K]>>
    : FormControl<T[K]>;
};

export type ControlInterface<T> = NonNullableControlInterface<Required<T>>;

export type MixedFormControlsInterface<T> = {
  [K in keyof T]-?: NonNullable<T[K]> extends Array<infer U>
    ? U extends object
      ? FormArray<FormGroup<MixedFormControlsInterface<NonNullable<U>>>>
      : FormArray<FormControl<U>>
    : NonNullable<T[K]> extends object
      ? FormGroup<MixedFormControlsInterface<NonNullable<T[K]>>>
      : FormControl<T[K]>;
};

export type DeepRequired<T> = {
  [K in keyof T]-?: T[K] extends object ? DeepRequired<Required<T[K]>> : Required<T[K]>;
};

export type DeepRequiredNonNullable<T> = {
  [K in keyof T]-?: T[K] extends (infer U)[]
    ? DeepRequiredNonNullable<U>[]
    : T[K] extends object
      ? DeepRequiredNonNullable<NonNullable<T[K]>>
      : NonNullable<T[K]>;
};

export interface Option<T> {
  label: string;
  value: T;
  disabled?: boolean;
}

export function createOptionsByEnum<T>(data: { [key: string]: T }): Option<T>[] {
  return Object.keys(data).map(key => {
    return {
      label: key,
      value: data[key],
    } satisfies Option<T>;
  });
}

/**
 * Custom ErrorStateMatcher
 */
export class GlobalErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: AbstractControl | null): boolean {
    return (control?.invalid && control.dirty) ?? false;
  }
}

/**
 * Turns a base64 Vermittler logo into a `data:` URI by sniffing its format from
 * the leading bytes. Returns `undefined` for a missing logo or an unrecognized
 * format (so callers can fall back to a default logo).
 */
export function vermittlerLogoDataUri(logo: string | undefined): string | undefined {
  if (!logo) return undefined;

  // PNG
  if (logo.startsWith('iVBORw0KGgo')) return `data:image/png;base64,${logo}`;
  // JPEG
  if (logo.startsWith('/9j/')) return `data:image/jpeg;base64,${logo}`;
  // GIF
  if (logo.startsWith('R0lGODlh')) return `data:image/gif;base64,${logo}`;
  // SVG (base64 of '<svg' / '<?xml')
  if (logo.startsWith('PHN2Z') || logo.startsWith('PD94bW'))
    return `data:image/svg+xml;base64,${logo}`;

  return undefined;
}

export const getJanuaryFirstOfNextYear = () => {
  const today = new Date();
  const nextYear = today.getFullYear() + 1;
  return new Date(nextYear, 0, 1);
};

type TypeMixedForm = FormGroup | FormArray;

export function controlMarkAsTouched(
  control: AbstractControl,
  options?: {
    onlySelf?: boolean;
    emitEvent?: boolean;
  }
) {
  control.markAsTouched(options);
  control.markAsDirty(options);
  control.updateValueAndValidity();
}

export function formMarkAllAsTouched(
  form: TypeMixedForm,
  options?: {
    onlySelf?: boolean;
    emitEvent?: boolean;
  }
) {
  Object.keys(form.controls).forEach(field => {
    const control = form.get(field);

    if (control instanceof FormGroup || control instanceof FormArray) {
      formMarkAllAsTouched(control as TypeMixedForm, options);
      return;
    }
    if (control) controlMarkAsTouched(control);
  });
}

/**
 * Scrolls the first invalid form control within `container` into view and
 * focuses it. Use on submit so the user is taken to the field that blocks them.
 *
 * Relies on Angular's `.ng-invalid` class, so call this *after* the form has
 * been marked as touched and change detection has applied the class (e.g. from
 * within a `setTimeout`/`afterNextRender`).
 *
 * @param container element to search within; defaults to the whole document.
 * @returns the control scrolled to, or `null` when nothing invalid was found.
 */
export function scrollToFirstInvalidControl(container: ParentNode = document): HTMLElement | null {
  // Target actual controls, not the wrapping form group elements (which also
  // carry `.ng-invalid` and appear earlier in document order).
  const selector = [
    'input.ng-invalid',
    'select.ng-invalid',
    'textarea.ng-invalid',
    'mat-select.ng-invalid',
    'mat-checkbox.ng-invalid',
  ].join(', ');

  const control = container.querySelector<HTMLElement>(selector);
  if (!control) return null;

  control.scrollIntoView({ behavior: 'smooth', block: 'center' });
  control.focus?.({ preventScroll: true });

  return control;
}

export function isEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;

  if (a === null || b === null || typeof a !== typeof b) return false;

  if (typeof a !== 'object') return false;

  // Compare dates
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Compare arrays
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    return a.every((val, i) => isEqual(val, b[i]));
  }

  // Compare plain objects
  if (typeof a === 'object' && typeof b === 'object') {
    const aObj = a as Record<string, unknown>;
    const bObj = b as Record<string, unknown>;

    const aKeys = Object.keys(aObj);
    const bKeys = Object.keys(bObj);

    if (aKeys.length !== bKeys.length) return false;

    for (const key of aKeys) {
      if (!isEqual(aObj[key], bObj[key])) return false;
    }

    return true;
  }

  return false;
}

export type Nullable<T> = {
  [P in keyof T]: T[P] | null;
};

export function clickThrough(isOverlayOpen: () => boolean, targetSelector: string): void {
  const destroyRef = inject(DestroyRef);
  const onCapturedPointerDown = (event: PointerEvent): void => {
    if (!isOverlayOpen()) return;
    const button = document.querySelector<HTMLButtonElement>(targetSelector);
    if (!button) return;
    const rect = button.getBoundingClientRect();
    const withinButton =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    if (withinButton) setTimeout(() => button.click());
  };
  document.addEventListener('pointerdown', onCapturedPointerDown, { capture: true });
  destroyRef.onDestroy(() =>
    document.removeEventListener('pointerdown', onCapturedPointerDown, { capture: true })
  );
}
